import { useEffect, useRef, useMemo } from 'react';

import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { useServerEvents } from '#lib/useServerEvents';

import { getGenerationStatus, getPendingContacts } from '../../api/aiCampaignApi';
import { fetchGeneratedEmails } from '../../api/emailCampaignsApi';

import { aiCampaignKeys } from './keys';

const PAGE_SIZE = 50;
// During generation many emails:insert events arrive in quick succession. Coalesce them into a
// single refetch so we don't fire one network request per inserted email.
const REALTIME_INVALIDATE_DEBOUNCE_MS = 500;

/**
 * Hook to get AI campaign generation status
 * Automatically polls every 2s while generating
 */
export const useGenerationStatus = (campaignId, options = {}) => {
	return useQuery({
		queryKey: aiCampaignKeys.status(campaignId),
		queryFn: () => getGenerationStatus(campaignId),
		refetchInterval: (query) => {
			const data = query.state.data;
			// Keep polling as long as the campaign is in the generating state. The job may sit in the
			// queue (waiting/delayed) for a while before the worker picks it up, so we must not gate
			// polling on jobStatus.state === 'active' — that would stop polling during the queue wait
			// and the progress UI would never appear.
			if (data?.status !== 'generating') return false;

			// Stop once the job has reached a terminal state, or if the job is gone entirely
			// (jobStatus === null). The backend reconciles an orphaned 'generating' to 'failed', but
			// guard here too so a stale response can never trap us in an endless poll loop.
			const jobState = data?.jobStatus?.state;
			if (!jobState || jobState === 'completed' || jobState === 'failed') return false;

			return 2000; // Poll every 2 seconds
		},
		enabled: Boolean(campaignId),
		...options,
	});
};

/**
 * Hook to get generated emails with infinite scroll pagination, kept fresh via SSE during generation.
 *
 * The server-side paginated fetch is the SINGLE source of truth for the list. SSE is used only as a
 * change signal: each emails:insert (coalesced) invalidates the query so React Query refetches the
 * authoritative, fully-related data. We deliberately do NOT mutate the cache from the SSE payload —
 * client-side prepending over offset pagination drifts page boundaries (newly inserted rows shift the
 * window), and hand-enriching the payload diverges from the server shape. Invalidate-and-refetch
 * sidesteps both, so there is no dedup, no pagination bookkeeping, and no contact-lookup plumbing.
 */
export const useGeneratedEmailsWithRealtime = (campaignId, isGenerating = false) => {
	const queryClient = useQueryClient();
	const prevIsGenerating = useRef(isGenerating);
	const invalidateTimerRef = useRef(null);

	const query = useInfiniteQuery({
		queryKey: aiCampaignKeys.emails(campaignId),
		queryFn: async ({ pageParam = 1 }) => {
			const result = await fetchGeneratedEmails(campaignId, { page: pageParam, limit: PAGE_SIZE });
			if (result.error) throw result.error;
			return {
				emails: result.data,
				pagination: result.pagination,
			};
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage.pagination;
			return page < totalPages ? page + 1 : undefined;
		},
		enabled: Boolean(campaignId),
	});

	const topics = useMemo(
		() => (campaignId && isGenerating ? [`emails:${campaignId}`] : []),
		[campaignId, isGenerating],
	);

	useServerEvents(topics, {
		enabled: Boolean(campaignId) && isGenerating,
		onEvent: (eventType) => {
			if (eventType !== 'emails:insert') return;

			// Coalesce a burst of inserts into one refetch.
			if (invalidateTimerRef.current) return;
			invalidateTimerRef.current = setTimeout(() => {
				invalidateTimerRef.current = null;
				queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emails(campaignId) });
			}, REALTIME_INVALIDATE_DEBOUNCE_MS);
		},
	});

	// Clear a pending debounce timer on unmount / campaign change.
	useEffect(() => {
		return () => {
			if (invalidateTimerRef.current) {
				clearTimeout(invalidateTimerRef.current);
				invalidateTimerRef.current = null;
			}
		};
	}, [campaignId]);

	// On completion, ensure a final fresh load and refresh pending contacts (they now have emails).
	useEffect(() => {
		if (prevIsGenerating.current && !isGenerating) {
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.emails(campaignId) });
			queryClient.invalidateQueries({ queryKey: aiCampaignKeys.pendingContacts(campaignId) });
		}
		prevIsGenerating.current = isGenerating;
	}, [isGenerating, queryClient, campaignId]);

	return query;
};

/**
 * Hook to get contacts that don't have generated emails yet
 */
export const usePendingContacts = (campaignId, options = {}) => {
	return useQuery({
		queryKey: aiCampaignKeys.pendingContacts(campaignId),
		queryFn: () => getPendingContacts(campaignId),
		enabled: Boolean(campaignId),
		...options,
	});
};
